import { useNavigate } from "react-router-dom";

interface UserInfoHeaderProps {
  user: {
    id: string;
    name: string;
    phone: string;
    address: string;
    accountDate: string;
    status: string;
    balance: number;
    monthlyUsage: number;
    yearlyUsage: number;
    safetyGrade: string;
    safetyLevel: number;
    insurance: { valid: boolean; expiry: string; company: string };
    category?: string;
    idCard?: string;
    contact?: string;
    type?: string;
    license?: string;
  };
}

export default function UserInfoHeader({ user }: UserInfoHeaderProps) {
  const navigate = useNavigate();
  const isResident = !user.type;

  const gradeColor = (g: string) => {
    if (g === "A+") return "text-green-600 bg-green-50 border-green-200";
    if (g === "A") return "text-teal-600 bg-teal-50 border-teal-200";
    if (g === "B") return "text-amber-600 bg-amber-50 border-amber-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Top gradient banner */}
      <div className="h-20 bg-gradient-to-r from-[#E8531A] via-[#f06a30] to-[#f5a06a] relative">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)", backgroundSize: "40px 40px" }}>
        </div>
      </div>

      <div className="px-6 pb-5">
        {/* Avatar + basic info */}
        <div className="flex items-end justify-between -mt-8 mb-4">
          <div className="flex items-end gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white border-4 border-white shadow-md flex items-center justify-center flex-shrink-0">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E8531A] to-[#f5a06a] flex items-center justify-center">
                <span className="text-white text-xl font-bold">{user.name[0]}</span>
              </div>
            </div>
            <div className="pb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold text-gray-900">{user.name}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${gradeColor(user.safetyGrade)}`}>
                  安全 {user.safetyGrade}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${user.status === "正常" ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
                  {user.status}
                </span>
                {user.type && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-orange-50 text-[#E8531A]">{user.type}</span>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                用户编号：{user.id} · 开户日期：{user.accountDate}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <button
              onClick={() => navigate(isResident ? "/profile/resident" : "/profile/commercial")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>返回列表
            </button>
            <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#E8531A] text-white rounded-lg hover:bg-[#d44a15] cursor-pointer whitespace-nowrap">
              <i className="ri-edit-line"></i>编辑档案
            </button>
            <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#E8531A] text-[#E8531A] rounded-lg hover:bg-orange-50 cursor-pointer whitespace-nowrap">
              <i className="ri-gas-station-line"></i>立即购气
            </button>
          </div>
        </div>

        {/* Info grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="space-y-1">
            <p className="text-xs text-gray-400">联系电话</p>
            <p className="text-sm font-medium text-gray-800">{user.phone}</p>
          </div>
          {isResident ? (
            <div className="space-y-1">
              <p className="text-xs text-gray-400">身份证号</p>
              <p className="text-sm font-medium text-gray-800 font-mono">
                {user.idCard ? user.idCard.replace(/(\d{6})\d{8}(\d{4})/, "$1********$2") : "-"}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              <p className="text-xs text-gray-400">营业执照</p>
              <p className="text-sm font-medium text-gray-800 font-mono text-xs">{user.license}</p>
            </div>
          )}
          <div className="space-y-1 col-span-2">
            <p className="text-xs text-gray-400">用气地址</p>
            <p className="text-sm font-medium text-gray-800">{user.address}</p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-3 pt-4 border-t border-gray-50">
          <div className="text-center">
            <p className="text-xs text-gray-400 mb-1">账户余额</p>
            <p className={`text-lg font-bold ${user.balance < 100 ? "text-red-600" : "text-[#E8531A]"}`}>
              ¥{user.balance.toFixed(2)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-400 mb-1">本月用气</p>
            <p className="text-lg font-bold text-gray-900">{user.monthlyUsage.toLocaleString()} <span className="text-xs font-normal text-gray-400">m³</span></p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-400 mb-1">年度用气</p>
            <p className="text-lg font-bold text-gray-900">{user.yearlyUsage.toLocaleString()} <span className="text-xs font-normal text-gray-400">m³</span></p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-400 mb-1">保险状态</p>
            <p className={`text-sm font-bold ${user.insurance.valid ? "text-green-600" : "text-red-500"}`}>
              {user.insurance.valid ? `有效` : "已过期"}
            </p>
            <p className="text-xs text-gray-400">{user.insurance.company}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
